import React, { useEffect, useState } from "react";
import { SteamAchievements, UIConstants } from "../../../shared/constants";
import { GameClient } from "../gameClient";
import { Modal } from "./Modal";
import { getUserId, getUserSettings } from "../userSettings";

interface ChecklistProps {
	dismiss: () => void;
}

declare var gameClient: GameClient;

const CheckboxItem = ({ id, children, noRightMargin }: {
	id: string;
	children: React.ReactNode;
	noRightMargin?: boolean;
}) => {
	const [showStamp, setShowStamp] = useState(false);

	const hasAchievement = getUserSettings().account.achievements.includes(id);
	const justAcheivedList = getUserSettings().unannouncedAchievements;
	const isJustAchievedIndex = justAcheivedList.indexOf(id);
	const isJustAchieved = isJustAchievedIndex >= 0;
	const isFilled = (hasAchievement && (isJustAchievedIndex === -1)) || (isJustAchieved && showStamp);

	useEffect(() => {
		if (isJustAchieved) {
			const timer = setTimeout(() => {
				setShowStamp(true);
			}, 700 * (isJustAchievedIndex + 1));
			return () => clearTimeout(timer);
		}
	}, [isJustAchieved]);

	return (
		<div style={{
			display: 'flex',
			alignItems: 'flex-start',
			marginBottom: '12px',
			fontFamily: 'NotoSans, monospace'
		}}>
			<div style={{
				width: '20px',
				height: '20px',
				border: '2px solid #000',
				marginRight: `${noRightMargin ? '0px' : '12px'}`,
				marginTop: '2px',
				backgroundColor: isFilled ? '#000' : 'transparent',
				transition: isJustAchieved ? 'none' : 'background-color 0.3s ease',
				animation: isJustAchieved && showStamp ? 'stamp 0.4s ease-out' : 'none'
			}} />
			<span style={{ fontSize: '14px', lineHeight: '1.4' }}>{children}</span>
		</div>
	);
};

export const AchievementChecklist = (props: ChecklistProps) => {
	return (
		<Modal isOpen={true} onClose={props.dismiss}>
			<div style={{
				backgroundColor: 'rgba(248, 248, 240, 0.5)',
				padding: '30px',
				fontFamily: 'NotoSans',
				color: '#333',
				boxShadow: 'inset 0 0 20px rgba(0,0,0,0.1)',
				border: '1px solid #ddd',
			}}>
				<style>{`
					@keyframes stamp {
						0% { transform: scale(1); }
						50% { transform: scale(1.5); }
						100% { transform: scale(1); }
					}
				`}</style>

				<h2 style={{ fontFamily: 'NotoSans', marginBottom: '20px', textAlign: "center" }}>OBJECTIVES</h2>

				<h3 style={{ fontFamily: 'NotoSans', marginTop: '20px', marginBottom: '10px' }}>SOLO</h3>
				<CheckboxItem id={SteamAchievements.WinFirstGame}>
					Win a game in Solo mode
				</CheckboxItem>
				<CheckboxItem id={SteamAchievements.Round4Endless}>
					Survive 4 rounds into Endless Mode
				</CheckboxItem>
				<CheckboxItem id={SteamAchievements.StayUnder10Cells}>
					Win a game in Solo mode with less than 10 immune cells
				</CheckboxItem>

				<h3 style={{ fontFamily: 'NotoSans', marginTop: '20px', marginBottom: '10px' }}>RANKED</h3>
				<div style={{ marginBottom: '10px' }}>Win a game at rank:</div>
				<div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
					{[1, 2, 3, 4, 5].map(rank => (
						<div key={rank} style={{ textAlign: 'center' }}>
							<div style={{ marginBottom: '5px' }}>{rank}</div>
							<CheckboxItem
								id={SteamAchievements.ReachedRank1.replace('1', rank.toString())}
								noRightMargin={true}
							>
								{""}
							</CheckboxItem>
						</div>
					))}
				</div>

				<h3 style={{ fontFamily: 'NotoSans', marginTop: '20px', marginBottom: '10px' }}>IN ONE TURN</h3>
				<CheckboxItem id={SteamAchievements.Summoner}>
					Summon 10 immune cells in combat
				</CheckboxItem>
				<CheckboxItem id={SteamAchievements.BigShock}>
					Kill 100 viruses with electricity
				</CheckboxItem>
				<CheckboxItem id={SteamAchievements.BigExplosion}>
					Kill 20 viruses with one explosion
				</CheckboxItem>
				<CheckboxItem id={SteamAchievements.Connector}>
					Have 8 or more connected immune cells
				</CheckboxItem>
			</div>
		</Modal>
	)
}

interface ChecklistIconProps {
	onClick: () => void;
	isAnimatingOut?: boolean;
	isAnimatingIn?: boolean;
}

export const ChecklistIcon = ({ onClick, isAnimatingOut, isAnimatingIn }: ChecklistIconProps) => {
	return (
		<div
			onClick={onClick}
			style={{
				position: 'absolute',
				bottom: '20px',
				right: '20px',
				width: '80px',
				height: '120px',
				backgroundColor: 'rgba(248, 248, 240, 0.9)',
				border: '2px solid #333',
				borderRadius: '8px',
				cursor: 'pointer',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
				zIndex: 10,
				animation: isAnimatingIn
					? `iconSlideIn ${UIConstants.ChecklistTransitionSeconds}s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`
					: 'none'
			}}
			onMouseEnter={(e) => {
				if (!isAnimatingOut && !isAnimatingIn) {
					e.currentTarget.style.transform = 'scale(1.1)';
				}
			}}
			onMouseLeave={(e) => {
				if (!isAnimatingOut && !isAnimatingIn) {
					e.currentTarget.style.transform = 'scale(1)';
				}
			}}
		>
			<style>{`
				@keyframes iconSlideIn {
					0% { 
						transform: translate(-40vw, -40vh) scale(7.0, 6.0);
					}
					100% { 
						transform: translate(0, 0) scale(1);
					}
				}
			`}</style>
			{/* Checklist icon - scalable SVG */}
			{!(isAnimatingIn || isAnimatingOut) && <svg
				width="24"
				height="36"
				viewBox="0 0 24 36"
				fill="none"
				style={{ transform: 'scale(2)' }}
			>
				<rect x="2" y="4" width="3" height="3" stroke="#333" strokeWidth="1.0" fill="none" />
				<rect x="2" y="10" width="3" height="3" stroke="#333" strokeWidth="1.0" fill="#333" />
				<rect x="2" y="16" width="3" height="3" stroke="#333" strokeWidth="1.0" fill="none" />
				<rect x="2" y="22" width="3" height="3" stroke="#333" strokeWidth="1.0" fill="none" />
				<rect x="2" y="28" width="3" height="3" stroke="#333" strokeWidth="1.0" fill="none" />

				<line x1="8" y1="5.5" x2="20" y2="5.5" stroke="#333" strokeWidth="1.5" />
				<line x1="8" y1="11.5" x2="20" y2="11.5" stroke="#333" strokeWidth="1.5" />
				<line x1="8" y1="18" x2="20" y2="18" stroke="#333" strokeWidth="1.5" />
				<line x1="8" y1="23.5" x2="20" y2="23.5" stroke="#333" strokeWidth="1.5" />
				<line x1="8" y1="29.5" x2="20" y2="29.5" stroke="#333" strokeWidth="1.5" />
			</svg>}
		</div>
	);
};